import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { CountryModel } from 'src/app/models/management/country.model';
import { EnterpriseCityModel } from 'src/app/models/management/enterprise-city.model';
import { EnterpriseModel } from 'src/app/models/management/enterprise.model';
import { CityService } from 'src/app/services/management/city.service';
import { CountryService } from 'src/app/services/management/country.service';
import { DepartmentService } from 'src/app/services/management/department.service';
import { EnterpriseCityService } from 'src/app/services/management/enterprise-city.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-enterprise-city',
  templateUrl: './register-enterprise-city.component.html',
  styleUrls: ['./register-enterprise-city.component.scss'],
  standalone: false
})
export class RegisterEnterpriseCityComponent implements OnInit {
  @Input() disabled: boolean = false;
  @Input() permissionUpdatable: boolean = false;
  @Input() enterpriseContract: EnterpriseModel = new EnterpriseModel();
  @Input('enterpriseCityContract')
  set changeEnterpiseCity(changeEnterpriseCity: EnterpriseCityModel) {
    this.changeEnterpriseCity(changeEnterpriseCity);
  }
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  enterpriseCityContract: EnterpriseCityModel = new EnterpriseCityModel();
  enterpriseCityId: number = 0;
  cityId: number = 0;
  departmentId: number = 0;
  countryModel: CountryModel = new CountryModel();
  formEnterpriseCity: FormGroup = new FormGroup({});
  listDepartments: any[] = [];
  listCitiesByCountry: any[] = [];
  listCities: any[] = [];
  listObservers: Subscription[] = [];

  constructor(private cityService: CityService,
    private countryService: CountryService,
    private departmentService: DepartmentService,
    private enterpriseCityService: EnterpriseCityService,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService
  ) { }

  async ngOnInit() {
    this.validateForm(new EnterpriseCityModel());
    await this.getCountryByUserAuthenticated();
  }


  async changeCity(cityId: number | null) {
    if (cityId) {
      await this.getEnterpriseCityByKeys(cityId);

    }
  }

  async changeDepartment(departmentId: number | null) {
    if (departmentId) {
      await this.getAllCitiesByDepartmentId(departmentId);
      this.validateForm(new EnterpriseCityModel);
      this.formEnterpriseCity.controls["departmentId"].patchValue(departmentId);
    } else {
      this.listCities = this.listCitiesByCountry;
    }
  }

  async changeEnterpriseCity(changeEnterpiseCity: EnterpriseCityModel) {
    if (changeEnterpiseCity.enterpriseCityId != 0) {
      this.enterpriseCityContract = changeEnterpiseCity;
    }
    this.validateForm(changeEnterpiseCity);
  }

  async getAllCitiesByDepartmentId(departmentId: number) {
    this.listCities = await firstValueFrom(this.cityService.getAllCitiesByDepartmentIdForSelect(departmentId));

  }

  async getAllCitiesByCountry(countryId: number) {
    if (countryId) {
      this.listCitiesByCountry = await firstValueFrom(this.cityService.getAllCitiesByCountryIdForSelectAsync(countryId));
      this.listCities = this.listCitiesByCountry;
    }
  }

  async getAllDepartments() {
    if (this.countryModel.countryId) {
      this.listDepartments = await firstValueFrom(this.departmentService.getAllDepartmentsByCountryIdForSelect(this.countryModel.countryId));

    }
  }

  async getEnterpriseCityByKeys(cityId: number) {
    this.enterpriseCityContract = await firstValueFrom(this.enterpriseCityService.getEnterpriseCityByKeys(this.enterpriseContract.enterpriseId, cityId));

    this.validateForm(this.enterpriseCityContract);
  }

  async cancel() {
    this.cityId = 0;
    this.enterpriseCityContract = new EnterpriseCityModel();
    this.validateForm(this.enterpriseCityContract);
    this.listCities = this.listCitiesByCountry;
    this.isFormView.emit(false);
  }

  async getCountryByUserAuthenticated() {
    this.countryModel = await firstValueFrom(this.countryService.getCountryByUserAuthenticated());
    await Promise.all([
      this.getAllDepartments(),
      this.getAllCitiesByCountry(this.countryModel.countryId)
    ]);
  }

  async send(): Promise<any> {
    if (this.formEnterpriseCity.valid) {
      const modelEnterpriseCity = this.formEnterpriseCity.getRawValue();
      try {
        if (this.enterpriseCityId == 0) {
          await firstValueFrom(this.enterpriseCityService.createEnterpriseCity(modelEnterpriseCity));
          this.toastHandlerService.showToast('success', 'El detalle fue creado exitosamente.', '');
        }
        else {
          this.enterpriseCityContract.swIcaSelfRetainer = modelEnterpriseCity.swIcaSelfRetainer;
          await firstValueFrom(this.enterpriseCityService.updateEnterpriseCity(modelEnterpriseCity));
          this.toastHandlerService.showToast('success', 'El detalle fue modficado exitosamente.', '');
        }

        this.enterpriseCityContract = new EnterpriseCityModel();
        this.validateForm(this.enterpriseCityContract)
        this.isFormView.emit(true);
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al guardar el detalle.', error);
      }
      this.listCities = this.listCitiesByCountry;
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      cityId: {
        required: 'El campo ciudad es obligatorio.'
      },
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formEnterpriseCity.get(controlName);

      if (control && control.invalid && control.errors) {
        const errors = control.errors;

        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm(enterpriseCity: EnterpriseCityModel) {
    this.formEnterpriseCity = this.formBuilder.group({
      enterpriseId: [enterpriseCity.enterpriseId, [Validators.required]],
      departmentId: [[]],
      cityId: [enterpriseCity.cityId, [Validators.required, Validators.min(1)]],
      swIcaSelfRetainer: [enterpriseCity.swIcaSelfRetainer, []]
    });

    this.enterpriseCityId = enterpriseCity.enterpriseCityId;
    if (enterpriseCity.cityId != 0) {
      const city = this.listCitiesByCountry.find(x => x.value == enterpriseCity.cityId)
      this.formEnterpriseCity.controls["departmentId"].patchValue(city.departmentId);
    }
  }

}
